import { create } from 'zustand'
import type { Theme, SidebarSection, ChatMessage, ActivityEvent } from '@/types'

const INIT_MSG: ChatMessage = { id: 'msg_init', role: 'system', content: 'Connected to Ops-Bridge. Live endpoint mode.', timestamp: new Date() }
const THEME_KEY = 'opsbridge-theme'

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'auto'
  try {
    const saved = localStorage.getItem(THEME_KEY)
    return saved === 'light' || saved === 'dark' || saved === 'auto' ? saved : 'auto'
  } catch {
    return 'auto'
  }
}

interface AppState {
  theme: Theme
  setTheme: (theme: Theme) => void
  resolvedTheme: 'light' | 'dark'
  setResolvedTheme: (theme: 'light' | 'dark') => void

  environmentMode: 'live' | 'sandbox'
  setEnvironmentMode: (mode: 'live' | 'sandbox') => void

  sidebarCollapsed: boolean
  toggleSidebar: () => void

  chatPanelCollapsed: boolean
  toggleChatPanel: () => void

  activeSection: SidebarSection
  setActiveSection: (section: SidebarSection) => void

  contextPanelOpen: boolean
  toggleContextPanel: () => void

  chatMessages: ChatMessage[]
  addChatMessage: (message: ChatMessage) => void
  setChatMessages: (messages: ChatMessage[]) => void

  activeSessionId: string | null
  setActiveSessionId: (id: string | null) => void

  activeProjectId: string | null
  setActiveProjectId: (id: string | null) => void

  selectedChatSessionId: string | null
  setSelectedChatSessionId: (id: string | null) => void

  sessionListVersion: number
  bumpSessionList: () => void

  globalRefreshVersion: number
  bumpGlobalRefresh: () => void

  activityFeed: ActivityEvent[]
  addActivity: (event: ActivityEvent) => void


  timerWidget: { open: boolean; status: 'running' | 'stopped' | 'none'; label: string | null; elapsedMs: number; lastServerUpdate: number }
  setTimerWidget: (state: Partial<AppState['timerWidget']>) => void
  closeTimerWidget: () => void
}

export const useAppStore = create<AppState>((set) => ({
  theme: readStoredTheme(),
  setTheme: (theme) => {
    if (typeof window !== 'undefined') {
      try { localStorage.setItem(THEME_KEY, theme) } catch { /* device storage unavailable */ }
    }
    set({ theme })
  },
  resolvedTheme: 'dark',
  setResolvedTheme: (resolvedTheme) => set({ resolvedTheme }),

  environmentMode: 'live',
  setEnvironmentMode: (environmentMode) => set({ environmentMode }),

  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

  chatPanelCollapsed: false,
  toggleChatPanel: () => set((s) => ({ chatPanelCollapsed: !s.chatPanelCollapsed })),

  activeSection: 'dashboard' as SidebarSection,
  setActiveSection: (activeSection) => set({
    activeSection,
    ...(activeSection === 'chat' ? { selectedChatSessionId: null } : {}),
  }),

  contextPanelOpen: true,
  toggleContextPanel: () => set((s) => ({ contextPanelOpen: !s.contextPanelOpen })),

  chatMessages: [INIT_MSG],
  addChatMessage: (message) =>
    set((s) => ({ chatMessages: [...s.chatMessages, message] })),
  setChatMessages: (messages) => set({ chatMessages: messages }),

  activeSessionId: (typeof window !== 'undefined' ? localStorage.getItem('ops_active_session') : null) ?? null,
  setActiveSessionId: (activeSessionId) => {
    if (typeof window !== 'undefined') {
      if (activeSessionId) localStorage.setItem('ops_active_session', activeSessionId)
      else localStorage.removeItem('ops_active_session')
    }
    set({ activeSessionId })
  },

  activeProjectId: null,
  setActiveProjectId: (activeProjectId) => set({ activeProjectId }),

  selectedChatSessionId: null,
  setSelectedChatSessionId: (selectedChatSessionId) => set({ selectedChatSessionId }),

  sessionListVersion: 0,
  bumpSessionList: () => set((s) => ({ sessionListVersion: s.sessionListVersion + 1 })),

  globalRefreshVersion: 0,
  bumpGlobalRefresh: () => set((s) => ({ globalRefreshVersion: s.globalRefreshVersion + 1 })),

  activityFeed: [],
  addActivity: (event) =>
    set((s) => ({ activityFeed: [event, ...s.activityFeed].slice(0, 100) })),


  timerWidget: { open: false, status: 'none', label: null, elapsedMs: 0, lastServerUpdate: 0 },
  setTimerWidget: (state) => set((s) => ({ timerWidget: { ...s.timerWidget, ...state } })),
  closeTimerWidget: () => set((s) => ({ timerWidget: { ...s.timerWidget, open: false } })),
}))
